import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';

import {
  statData
} from './data';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { Model } from 'echarts';
import { TrainerService } from 'src/app/core/services/trainer.service';
import { DropFitnessSDKService } from 'src/app/core/services/dropfitnesssdk.service';
import { BookingSessionType, isCancelledPtBookingAction, Session, TrainerModel } from 'src/app/core/models/trainer.models';
import { ToastService } from 'src/app/core/services/toast-service';

import { AppSettings, StorageKey } from 'src/app/core/appsettings';

const toTicks = (stringDate: string, stringTime: string): number => {
  let date = new Date(stringDate);
  let datetime = `${date.toISOString().substring(0, 11)}${stringTime}`;
  return Date.parse(datetime);
}

const bookingIdentity = (item: any): string => {
  const bookingId =
    item?.BookingId ?? item?.bookingId ??
    item?.GroupSessionId ?? item?.groupSessionId ??
    item?.SessionGroupId ?? item?.sessionGroupId ??
    item?.SlotSessionId ?? item?.slotSessionId ??
    item?.SlotId ?? item?.slotId;
  if (bookingId != null && bookingId !== '') {
    return `bid:${bookingId}`;
  }
  return `slot:${item?.TrainerId ?? ''}|${item?.StartDate ?? ''}|${item?.StartTime ?? ''}|${item?.Duration ?? ''}|${item?.Type ?? ''}|${item?.Action ?? ''}`;
};

/** Feed rows may use MemberId, memberId, or Member.Id — normalize for filters. */
const sessionMemberId = (item: any): number => {
  const n = Number(
    item?.MemberId ?? item?.memberId ?? item?.Member?.Id ?? item?.member?.id ?? 0
  );
  return Number.isFinite(n) ? n : 0;
};

@Component({
  selector: 'app-training-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

/**
 * Training Dashboard Component
 */
export class TrainingDashboardComponent implements OnInit {

  UpcomingSessions!: any[];
  PastSessions!: any[];
  trainer!: TrainerModel;

  upcomingSelected: any;
  cancelBookingIds: number[] = [];
  /** When true, [cancelSession] uses one DELETE GroupSession for the anchor id in [cancelBookingIds]. */
  useGroupSessionCancel = false;
  
  statData!: any;
  loading: boolean = false;

  constructor(
    private authService: AuthenticationService,
    private dfService: DropFitnessSDKService,
    private trainerService: TrainerService,
    private toastSevice: ToastService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.statData = statData;
    this.fetchData();
  }

   /**
   * Fetches the data
   */
  fetchData() {
    this.loading = true;
    this.cdr.markForCheck();
    this.dfService.ensureUrls().then(() => {
      var trainerStr = localStorage.getItem(StorageKey.CURRENT_USER);
      if(trainerStr)
      {
        this.trainer = JSON.parse(trainerStr);
        this.trainerService.getSessionsFeed(this.trainer.Id).then((payload: any) => {
          let upcoming = <Array<Session>>(payload?.Upcoming ?? payload?.upcoming ?? []);
          let past = <Array<Session>>(payload?.Past ?? payload?.past ?? []);
          this.UpcomingSessions = upcoming.sort((a: Session, b: Session): number => toTicks(a.StartDate, a.StartTime) > toTicks(b.StartDate, b.StartTime) ? 1 : -1);
          this.PastSessions = past.sort((a: Session, b: Session): number => toTicks(a.StartDate, a.StartTime) > toTicks(b.StartDate, b.StartTime) ? -1 : 1);
          this.statData[0].value = this.PastSessions.filter(session => !isCancelledPtBookingAction(session.Action)).length;
          this.statData[1].value = payload?.Stats?.ActiveClients ?? payload?.stats?.activeClients ?? 0;
          this.loading = false;
          this.cdr.markForCheck();
        }).catch((error) => {
          this.loading = false;
          this.cdr.markForCheck();
          this.toastSevice.showDanger(error);
        });
      } else {
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  isLoading() {
    return this.loading;
  }

  isFuture(startDate: string, startTime: string): boolean {
    return toTicks(startDate, startTime) > Date.now();
  }

  isPast(startDate: string, startTime: string) {
    return toTicks(startDate, startTime) <= Date.now();
  }

  user() {
    return this.authService.currentAuthDetails();
  }

  greeting() {
    let g = 'Good ';
    let hour = new Date().getHours();
    if (hour < 12) {
      g += 'morning';
    } else if (hour < 18) {
      g += 'afternoon';
    } else {
      g += 'evening';
    }
    let user = this.user();
    if (user) {
      g += ', ' + user.FirstName;
    }
    return g + '!';
  }

  addSession(modal: any) {
    modal.open();
  }

  updateSession(session: any) {
    this.trainerService.updateSession(session.session)
      .then(() => {
        this.toastSevice.showSuccess('Session updated successfully.');
        this.fetchData();
      })
      .catch((error) => this.toastSevice.showDanger(error || 'Failed to update session.'));
  }

  onClickUpcoming(item: any, modal: any) {
    this.upcomingSelected = item;
    const related =
      item.Type === BookingSessionType.GroupPT
        ? this.upcomingSessionsByMemberId()
        : [item];
    modal.open(item, related);
  }

  onClickCompleted(item: any, modal: any) {
    if (!AppSettings.isPastSessionEditable(item)) {
      this.toastSevice.showDanger(
        'Past sessions can only be edited within 72 hours of the scheduled start time.'
      );
      return;
    }
    this.upcomingSelected = item;
    const related =
      item.Type === BookingSessionType.GroupPT
        ? this.pastSessionsRelatedToSelection()
        : [item];
    modal.open(item, related);
  }

  checkRefund(modal: any, sessions: any) {
    if (sessions?.cancelEntireGroupSession) {
      this.useGroupSessionCancel = true;
      const bid = sessions.bookingId ?? sessions.BookingId;
      this.cancelBookingIds = [bid];
      const has24hour = moment(sessions.startDateTime).isBefore(moment().add(AppSettings.REFUND_WINDOW, 'hours'));
      if (has24hour) {
        modal.open();
        return;
      }
      this.cancelSession([{ bookingId: bid, refund: true }]);
      return;
    }

    let s: any[] = Array.isArray(sessions) && sessions.length ? sessions : [sessions];
    let has24hour = false;
    this.useGroupSessionCancel = false;
    this.cancelBookingIds = [];
    s.forEach(session => {
      const bid = session.bookingId ?? session.BookingId ?? session.Id ?? session.id;
      this.cancelBookingIds.push(bid);
      if (moment(session.startDateTime).isBefore(moment().add(AppSettings.REFUND_WINDOW, 'hours'))) {
        has24hour = true;
      }
    });
    if (has24hour) {
      modal.open();
      return;
    }
        
    this.cancelSession(this.cancelBookingIds.map(bookingId => ({ bookingId, refund: true })));
  }

  cancelSession(refundOptions: any, index: number = 0) {
    this.loading = true;
    this.cdr.markForCheck();
    if (this.useGroupSessionCancel && index === 0 && refundOptions?.length) {
      this.trainerService.cancelGroupSession(refundOptions[0].bookingId, refundOptions[0].refund).then(() => {
        this.useGroupSessionCancel = false;
        this.fetchData();
      }).catch((err) => {
        this.useGroupSessionCancel = false;
        this.loading = false;
        this.cdr.markForCheck();
        this.toastSevice.showDanger(err);
      });
      return;
    }
    if (index === 0 && Array.isArray(refundOptions) && refundOptions.length > 1) {
      const refund = !!refundOptions[0]?.refund;
      const bookingIds = refundOptions.map(x => x.bookingId);
      this.trainerService.cancelSessionsBatch(bookingIds, refund).then(() => {
        this.fetchData();
      }).catch((err) => {
        this.loading = false;
        this.cdr.markForCheck();
        this.toastSevice.showDanger(err);
      });
      return;
    }
    this.trainerService.cancelSession(refundOptions[index].bookingId, refundOptions[index].refund).then(() => {
      if (index < refundOptions.length - 1) {
        this.cancelSession(refundOptions, index + 1);
      } else {
        this.fetchData();
      }
    }).catch((err) => {
      this.loading = false;
      this.cdr.markForCheck();
      this.toastSevice.showDanger(err);
    });
  }

  daysFromToday(days: number = 0) {
    let today = new Date();
    var yearFromDate = today.getFullYear();
    var monthFromYear = today.getMonth();
    var dayFromYear = 1; // defaulting to 1st of the month in case of non-existing date
    var newDate = new Date(yearFromDate, monthFromYear, dayFromYear + days);
    return newDate;
  }

  upcomingSessionsByMemberId(): Session[] {
    if (!this.upcomingSelected) return [];
    const sel = this.upcomingSelected as Session;
    if (sel.Type === BookingSessionType.GroupPT) {
      const selectedIdentity = bookingIdentity(sel);
      return this.UpcomingSessions.filter(s =>
        s.Action === 1
        && s.Type === BookingSessionType.GroupPT
        && bookingIdentity(s) === selectedIdentity
      );
    }
    const mid = sessionMemberId(sel);
    return this.UpcomingSessions.filter(
      session => sessionMemberId(session) === mid && session.Action === 1
    );
  }

  /** Same grouping as upcoming, for past sessions (group PT slot + cancel-all). */
  pastSessionsRelatedToSelection(): Session[] {
    if (!this.upcomingSelected || !this.PastSessions?.length) {
      return [];
    }
    const sel = this.upcomingSelected as Session;
    if (sel.Type === BookingSessionType.GroupPT) {
      const selectedIdentity = bookingIdentity(sel);
      return this.PastSessions.filter(s =>
        s.Action === 1
        && s.Type === BookingSessionType.GroupPT
        && bookingIdentity(s) === selectedIdentity
      );
    }
    const mid = sessionMemberId(sel);
    return this.PastSessions.filter(
      session => sessionMemberId(session) === mid && session.Action === 1
    );
  }

}
